import PageHeader from "../../components/layout/PageHeader.jsx";
import Location from "../../hooks/Location";

import { client } from "../../hooks/Client.js";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import ReportBuilderBody from "../../components/liveReport/reportBuilder2/components/ReportBuilderBody.jsx";
import { EditorContext, EditorContextProvider } from "../../components/liveReport/reportBuilder2/context/EditorContext.jsx";
import { CircularProgress } from "@mui/material";
import { LiveReportInfoCard } from "../../components/liveReport/reportBuilder2/components/LiveReportInfoCard.jsx";
import ShareReportModal from "../../components/liveReport/reportBuilder2/components/ShareReportModal.jsx";
import { getEditorUrl } from "../../components/liveReport/reportBuilder2/utils/editorUrl.js";
import duplicateReport from "../../components/liveReport/reportBuilder2/utils/duplicateReport";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { TemplateDataProvider } from "../../components/liveReport/reportBuilder2/context/TemplateDataContext.jsx";
import { LiveReportCrudContext } from "../../components/liveReport/reportBuilder2/context/LiveReportCrudContext.jsx";
import useLiveReportWidgets from "../../hooks/useLiveReportWidgets.js";
import { isSuperAdminTenant } from "../../utils/superAdminTenant.js";
import { ReportPeriodContext } from "../../components/liveReport/reportBuilder2/context/ReportPeriodContext.jsx";
import ReportPersonaContext from "../../StateManagment/ReportPersonaContext";
import reportToPeriodRange from "../../helpers/liveReportHelpers/reportToPeriodRange.js";
import isReportLocked from "../../helpers/liveReportHelpers/isReportLocked.js";

/**
 * @param {object} props
 * @param {boolean} props.isTemplate Whether the report being edited is a template. 
 * @param {string} [props.copyOfId] When set, loads widgets from the specified report ID instead of the URL's report ID, and clears docids so saves create new widgets. Used for "create from template" flows where the editor needs to load a template's widgets but create a new report.
 * @returns 
 */
const Wrapper = ({ isTemplate, copyOfId }) => {

    const history = useHistory();
    const editorContext = useContext(EditorContext);
    const liveReportCrudContext = useContext(LiveReportCrudContext);
    // De globale snackbar; die overleeft het openen van de kopie.
    const [, , , setSavedMessage] = useContext(SavedIcon);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    if (!editorContext || !liveReportCrudContext) {
        throw new Error("Required contexts are missing.");
    }

    const {
        reportId,
        report,
        createNewReportFromSource,
        updateReport,
    } = liveReportCrudContext;

    const {
        setWidgets,
    } = editorContext;

    const fetchedWidgets = useLiveReportWidgets({
        reportId: copyOfId ? copyOfId : reportId,
        report,
        asCopy: Boolean(copyOfId),
    });

    useEffect(() => {
        setWidgets(fetchedWidgets);
    }, [fetchedWidgets, setWidgets]);

    const periodRange = useMemo(() => reportToPeriodRange(report), [report]);

    if (!report) {
        return <CircularProgress size={24} />;
    }

    return (
        <>
            <div className="template-viewer-header">
                <LiveReportInfoCard
                    editable={true}
                    report={report}
                    // A locked report keeps its name: B4 leaves only the
                    // period, publishing/sharing and deleting.
                    onReportNameChange={isReportLocked(report) ? undefined : (newTitle) => {
                        if (!report) return;
                        updateReport(report, { Name: newTitle });
                    }}
                    handleSetStatus={async (report) => {
                        const newStatus = report.Status === 'published'
                            ? 'draft'
                            : 'published';
                        await updateReport(report, { Status: newStatus });
                    }}
                    handleDuplicate={() => duplicateReport({
                        report,
                        createNewReportFromSource,
                        navigate: history.push,
                        notify: setSavedMessage,
                    })}
                    handleNewFromTemplate={async () => {
                        const newReport = await createNewReportFromSource(report, false);
                        history.push(getEditorUrl(newReport.ID, false));
                    }}
                    handleShare={() => setShareModalOpen(true)}
                    // Alleen een template: welke modules een organisatie nodig
                    // heeft om hem in de lijst te zien (isTemplateAvailable).
                    onRequiredModulesChange={report.IsTemplate
                        ? (modules) => updateReport(report, { RequiredModules: modules })
                        : undefined}
                    // setDeleteModalOpen={setDeleteModalOpen}
                />
            </div>

            {/* Gemount bij openen: het venster leest de code en de deelnemer
                live van het rapport uit de context. */}
            {shareModalOpen && (
                <ShareReportModal
                    modalOpen={true}
                    setModalOpen={setShareModalOpen}
                    report={report}
                />
            )}

            <ReportPeriodContext.Provider value={periodRange}>
                <ReportPersonaContext.Provider value={report.PersonaID ?? null}>
                    <ReportBuilderBody report={report} widgets={fetchedWidgets} />
                </ReportPersonaContext.Provider>
            </ReportPeriodContext.Provider>
        </>
    );
}

const LivereportTemplateBuilder = () => {

    // Hooks
    const history = useHistory();
    const reportId = Location()[3];
    const params = new URLSearchParams(window.location.search);
    const isTemplate = params.get('template') === 'true';
    const copyOfId = params.get('copyOf');

    // Gate the template-builder URL to the SuperAdmin tenant. Sits above the
    // contexts so the editor never mounts (and never subscribes) for users
    // who aren't allowed to be here.
    useEffect(() => {
        if (isTemplate && !isSuperAdminTenant(client)) {
            history.push(`/${client}/livereports2`);
        }
    }, [isTemplate, history]);

    if (isTemplate && !isSuperAdminTenant(client)) {
        return null;
    }

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={isTemplate
                        ? 'Live rapportage template aanpassen'
                        : 'Live rapportage aanpassen'}
                    subHeading={''}
                    description={isTemplate
                        ? 'Hier kun je live rapportage templates creëren.'
                        : 'Hier kun je je live rapportage aanpassen.'}
                    AI={false}
                />

                <TemplateDataProvider isTemplate={isTemplate}>
                    <EditorContextProvider>
                        <Wrapper isTemplate={isTemplate} copyOfId={copyOfId} />
                    </EditorContextProvider>
                </TemplateDataProvider>
            </div>
        </div>
    )
}

export default LivereportTemplateBuilder